/**
 * Pieces shared by the Attributes, attribute edit, group and User groups pages —
 * built to the reference design (5 Oct 2026): headings on the grey page with a
 * subtitle, white cards, a ⋯ menu per row, setting rows with a switch on the right.
 */

import { useEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from 'react'
import { Cake, Calendar, ChevronDown, Globe, Hash, List, MapPin, MoreHorizontal, Smartphone, Tag, ToggleRight, Type, User } from 'lucide-react'
import { T } from '@/components/shell/orgTheme'
import { Btn, Skeleton } from '@/components/org/orgUi'
import { useAttrStatus, useFreshAttrs } from '@/lib/attributesData'
import { focusRing, inputStyle } from '@/components/org/orgHelpers'
import { PageHeader } from '@/components/shell/PageHeader'
import type { AttrDef } from '@/lib/attributesModel'

// ─── Page layout ─────────────────────────────────────────────────────────────

/**
 * A page in the app's standard frame — the same white header band as every other
 * page (one-line title, back arrow in the margin, main action on the right), then
 * the subtitle and the content (5 Oct 2026: "keep the heading the same for every page").
 */
export function OrgPage({ title, sub, back, backLabel, badges, action, children }: {
  title: string; sub?: ReactNode; back?: string; backLabel?: string; badges?: ReactNode; action?: ReactNode; children: ReactNode
}) {
  return (
    <>
      <PageHeader title={title} backTo={back} backLabel={backLabel} badges={badges} action={action} />
      <div style={{ maxWidth: 1280, margin: '0 auto', padding: '20px 24px 72px', display: 'flex', flexDirection: 'column', gap: 28 }}>
        {sub && <span style={{ fontSize: 15, color: T.muted, marginBottom: -8 }}>{sub}</span>}
        <SaveErrorBanner />
        {children}
      </div>
    </>
  )
}

export function SectionTitle({ children, right }: { children: ReactNode; right?: ReactNode }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
      <h2 style={{ margin: 0, flex: 1, fontSize: 19, fontWeight: 600, color: T.dark }}>{children}</h2>
      {right}
    </div>
  )
}

/** White card with a title — the edit pages' Details / Values / Settings. */
export function PageCard({ title, sub, children, style }: { title?: ReactNode; sub?: ReactNode; children: ReactNode; style?: CSSProperties }) {
  return (
    <section style={{ background: '#fff', border: `1px solid ${T.border}`, borderRadius: 16, padding: '24px 28px', display: 'flex', flexDirection: 'column', gap: 16, ...style }}>
      {(title || sub) && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {title && <h3 style={{ margin: 0, fontSize: 17, fontWeight: 600, color: T.dark }}>{title}</h3>}
          {sub && <span style={{ fontSize: 13.5, color: T.muted }}>{sub}</span>}
        </div>
      )}
      {children}
    </section>
  )
}

const listCard: CSSProperties = { background: '#fff', border: `1px solid ${T.border}`, borderRadius: 16, overflow: 'hidden' }

/** White rounded list container. */
export function ListCard({ children }: { children: ReactNode }) { return <div style={listCard}>{children}</div> }

/** Header chips: a filled one for the type, an outlined one for Standard / Custom. */
export function HeadChip({ children, outline }: { children: ReactNode; outline?: boolean }) {
  return (
    <span style={{ padding: '4px 14px', borderRadius: 999, fontSize: 14.5, fontWeight: 500, whiteSpace: 'nowrap',
      background: outline ? '#fff' : '#F1EFF4', color: T.ink, border: outline ? `1.5px solid ${T.inputBorder}` : '1.5px solid transparent' }}>{children}</span>
  )
}

// ─── Icons ───────────────────────────────────────────────────────────────────

export function TypeIcon({ def, size = 40 }: { def: AttrDef; size?: number }) {
  const Icon = def.key === 'dob' ? Cake
    : def.type === 'option' ? List : def.type === 'multi' ? Tag : def.type === 'location' ? MapPin
    : def.type === 'language' ? Globe : def.type === 'phone' ? Smartphone : def.type === 'date' ? Calendar
    : def.type === 'person' ? User : def.type === 'number' ? Hash : def.type === 'yesno' ? ToggleRight : Type
  return (
    <span style={{ width: size, height: size, flex: 'none', borderRadius: 10, background: T.tintSoft, color: T.purple, display: 'grid', placeItems: 'center' }}>
      <Icon size={size * 0.45} strokeWidth={1.8} />
    </span>
  )
}

// ─── Controls ────────────────────────────────────────────────────────────────

/** ⋯ button with a small menu. */
export function RowMenu({ items, label }: { items: Array<{ label: string; onClick: () => void; danger?: boolean } | false>; label: string }) {
  // Where the menu sits, measured from the button (6 Oct 2026). It is positioned against the
  // WINDOW (position: fixed), so a card with overflow hidden — the archived lists, tables — can
  // no longer cut it off; and it opens upward when there is no room below.
  const [pos, setPos] = useState<{ top: number; right: number } | null>(null)
  const open = !!pos
  const setOpen = (v: boolean) => { if (!v) setPos(null) }
  const box = useRef<HTMLDivElement>(null)
  const btn = useRef<HTMLButtonElement>(null)
  const list = items.filter(Boolean) as Array<{ label: string; onClick: () => void; danger?: boolean }>
  const toggle = () => {
    if (open || !btn.current) return setPos(null)
    const r = btn.current.getBoundingClientRect()
    const h = list.length * 37 + 10
    const up = r.bottom + 6 + h > window.innerHeight && r.top - 6 - h > 0
    setPos({ top: up ? r.top - 6 - h : r.bottom + 6, right: Math.max(8, window.innerWidth - r.right) })
  }
  useEffect(() => {
    if (!open) return
    const off = (e: MouseEvent) => { if (box.current && !box.current.contains(e.target as Node)) setPos(null) }
    const close = () => setPos(null)       // a fixed menu would drift from its button on scroll
    document.addEventListener('mousedown', off)
    window.addEventListener('scroll', close, true)
    window.addEventListener('resize', close)
    return () => { document.removeEventListener('mousedown', off); window.removeEventListener('scroll', close, true); window.removeEventListener('resize', close) }
  }, [open])
  return (
    <div ref={box} style={{ position: 'relative' }}>
      <button ref={btn} onClick={e => { e.stopPropagation(); toggle() }} aria-label={label} aria-haspopup="menu" aria-expanded={open} className="hover:bg-[#FAF9FB]"
        style={{ width: 38, height: 38, display: 'grid', placeItems: 'center', borderRadius: 10, border: `1px solid ${T.inputBorder}`, background: '#fff', color: T.ink, cursor: 'pointer' }}>
        <MoreHorizontal size={18} />
      </button>
      {pos && (
        <div role="menu" style={{ position: 'fixed', top: pos.top, right: pos.right, zIndex: 80, minWidth: 170, background: '#fff', border: `1px solid ${T.border}`, borderRadius: 10, boxShadow: '0 10px 30px rgba(28,26,34,.12)', padding: 4 }}>
          {list.map(i => (
            <button key={i.label} role="menuitem" onClick={e => { e.stopPropagation(); setOpen(false); i.onClick() }} className="hover:bg-[#F4F2F6]"
              style={{ width: '100%', textAlign: 'left', padding: '8px 12px', border: 'none', background: 'transparent', borderRadius: 7, cursor: 'pointer', fontSize: 14, color: i.danger ? T.red : T.dark }}>{i.label}</button>
          ))}
        </div>
      )}
    </div>
  )
}

/** Title + description on the left, a control on the right — the Settings card rows. */
export function SettingRow({ title, desc, control, first }: { title: string; desc?: string; control: ReactNode; first?: boolean }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap', padding: '16px 0', borderTop: first ? 'none' : '1px solid #F2F0F5' }}>
      <div style={{ flex: '1 1 260px', display: 'flex', flexDirection: 'column', gap: 3 }}>
        <span style={{ fontSize: 15, fontWeight: 600, color: T.dark }}>{title}</span>
        {desc && <span style={{ fontSize: 13.5, color: T.muted }}>{desc}</span>}
      </div>
      {control}
    </div>
  )
}

export function Segmented<V extends string>({ value, options, onChange, label }: { value: V; options: Array<[V, string]>; onChange: (v: V) => void; label: string }) {
  return (
    <div role="radiogroup" aria-label={label} style={{ display: 'flex', padding: 3, borderRadius: 10, background: '#F1EEF5' }}>
      {options.map(([v, l]) => (
        <button key={v} role="radio" aria-checked={value === v} onClick={() => onChange(v)}
          style={{ padding: '7px 14px', borderRadius: 8, border: 'none', cursor: 'pointer', fontSize: 14, whiteSpace: 'nowrap',
            background: value === v ? '#fff' : 'transparent', color: value === v ? T.purpleText : T.ink, fontWeight: value === v ? 500 : 400, boxShadow: value === v ? '0 1px 2px rgba(0,0,0,.08)' : 'none' }}>{l}</button>
      ))}
    </div>
  )
}

/** A collapsed "Archived … (n)  Show" row that opens its list. */
export function ArchivedRow({ label, count, children, inCard }: { label: string; count: number; children: ReactNode; inCard?: boolean }) {
  const [open, setOpen] = useState(false)
  if (!count) return null
  return (
    <div style={inCard ? { borderTop: '1px solid #F2F0F5' } : listCard}>
      <div style={{ display: 'flex', alignItems: 'center', padding: '16px 28px' }}>
        <span style={{ flex: 1, fontSize: 15, color: T.muted }}>{label} ({count})</span>
        <button onClick={() => setOpen(o => !o)} style={{ border: 'none', background: 'transparent', color: T.purpleText, fontSize: 15, textDecoration: 'underline', cursor: 'pointer' }}>{open ? 'Hide' : 'Show'}</button>
      </div>
      {open && <div style={{ borderTop: '1px solid #F2F0F5' }}>{children}</div>}
    </div>
  )
}

// ─── Pickers ─────────────────────────────────────────────────────────────────

const ellipsis = { maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } as const

export interface PickItem { id: string; label: string; hint?: string; muted?: boolean; /** what a muted item is — default "archived" */ mutedAs?: string }

/** Chips for what is picked, plus a searchable list to add more. */
/** Never put this inside a <label>: a click anywhere in the label is passed on to the first chip's ×. */
export function MultiPick({ items, value, onChange, placeholder = 'Add…', single, maxChips, label }: {
  items: PickItem[]; value: string[]; onChange: (v: string[]) => void; placeholder?: string; single?: boolean
  /** Names the whole picker (e.g. “Manager”) — for screen readers, and for tests, even once something is picked. */
  label?: string
  /** Show at most this many chips, then "+N more" (click to show all). */
  maxChips?: number
}) {
  const [open, setOpen] = useState(false)
  const [allChips, setAllChips] = useState(false)
  const [q, setQ] = useState('')
  const box = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (!open) return
    const off = (e: MouseEvent) => { if (box.current && !box.current.contains(e.target as Node)) setOpen(false) }
    document.addEventListener('mousedown', off)
    return () => document.removeEventListener('mousedown', off)
  }, [open])
  const byId = useMemo(() => new Map(items.map(i => [i.id, i])), [items])
  const shown = items.filter(i => !value.includes(i.id) && i.label.toLowerCase().includes(q.toLowerCase())).slice(0, 50)
  const add = (id: string) => { onChange(single ? [id] : [...value, id]); setQ(''); if (single) setOpen(false) }
  return (
    <div ref={box} style={{ position: 'relative' }} {...(label ? { role: 'group', 'aria-label': label } : {})}>
      <div onClick={() => setOpen(true)}
        style={{ ...inputStyle, minHeight: 42, padding: '6px 38px 6px 10px', position: 'relative', display: 'flex', flexWrap: 'wrap', gap: 5, alignItems: 'center', cursor: 'text', borderRadius: 10 }}>
        {(maxChips && !allChips ? value.slice(0, maxChips) : value).map(id => {
          const it = byId.get(id)
          return (
            <span key={id} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 4px 3px 10px', borderRadius: 999, fontSize: 13.5,
              background: it?.muted ? '#F1EFF4' : T.tint, color: it?.muted ? T.muted : '#3B2A55' }}>
              {it?.label ?? id}{it?.muted && <span style={{ fontSize: 11.5 }}>({it.mutedAs ?? 'archived'})</span>}
              <button onClick={e => { e.stopPropagation(); onChange(value.filter(x => x !== id)) }} aria-label={`Remove ${it?.label ?? id}`}
                style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: 'inherit', fontSize: 14, padding: '0 4px' }}>×</button>
            </span>
          )
        })}
        {!!maxChips && value.length > maxChips && (
          <button onClick={e => { e.stopPropagation(); setAllChips(a => !a) }}
            style={{ padding: '3px 10px', borderRadius: 999, border: `1px solid ${T.tintBorder}`, background: '#fff', color: T.purpleText, fontSize: 13, cursor: 'pointer' }}>
            {allChips ? 'Show fewer' : `+${value.length - maxChips} more`}
          </button>
        )}
        {(!single || value.length === 0) && (
          <input value={q} onChange={e => { setQ(e.target.value); setOpen(true) }} onFocus={() => setOpen(true)}
            // Enter picks the top match (7 Oct 2026) — typing a name and pressing Enter used to add
            // nothing while the words stayed in the box, looking picked. Backspace on empty removes the last.
            onKeyDown={e => {
              if (e.key === 'Enter') { e.preventDefault(); if (q.trim() && shown[0]) add(shown[0].id) }
              else if (e.key === 'Backspace' && !q && value.length && !single) onChange(value.slice(0, -1))
              else if (e.key === 'Escape') setOpen(false)
            }} placeholder={value.length ? (single ? '' : 'Add more…') : placeholder}
            aria-label={placeholder} style={{ flex: '1 1 80px', minWidth: 60, border: 'none', outline: 'none', fontSize: 14, padding: '4px 2px', background: 'transparent' }} />
        )}
        <ChevronDown size={16} color={T.muted} aria-hidden style={{ position: 'absolute', right: 12, top: '50%', transform: `translateY(-50%)${open ? ' rotate(180deg)' : ''}`, transition: 'transform .15s', pointerEvents: 'none' }} />
      </div>
      {open && (
        <div role="listbox" style={{ position: 'absolute', left: 0, right: 0, minWidth: 300, top: 'calc(100% + 4px)', zIndex: 60, maxHeight: 260, overflowY: 'auto', background: '#fff', border: `1px solid ${T.border}`, borderRadius: 10, boxShadow: '0 10px 30px rgba(28,26,34,.12)', padding: 4 }}>
          {shown.length ? shown.map(i => (
            <button key={i.id} role="option" aria-selected={false} onClick={() => add(i.id)} className="hover:bg-[#F4F2F6]"
              style={{ width: '100%', textAlign: 'left', display: 'flex', flexDirection: 'column', gap: 2, padding: '9px 14px', border: 'none', background: 'transparent', borderRadius: 7, cursor: 'pointer', fontSize: 14, color: i.muted ? T.muted : T.dark }}>
              {/* Name over email, one line each — side by side, a narrow picker wrapped the name and clipped the email. */}
              <span style={ellipsis}>{i.label}{i.muted ? ` (${i.mutedAs ?? 'archived'})` : ''}</span>
              {i.hint && <span style={{ ...ellipsis, fontSize: 12.5, color: T.faint }}>{i.hint}</span>}
            </button>
          )) : <div style={{ padding: '10px 12px', fontSize: 13.5, color: T.faint }}>{q ? 'No matches' : 'Nothing more to add'}</div>}
        </div>
      )}
    </div>
  )
}

export function Select({ value, onChange, children, label, style }: { value: string; onChange: (v: string) => void; children: ReactNode; label: string; style?: CSSProperties }) {
  return (
    <select value={value} onChange={e => onChange(e.target.value)} aria-label={label} className={`${focusRing} lb-select`}
      style={{ ...inputStyle, padding: '9px 10px', fontSize: 14, cursor: 'pointer', borderRadius: 10, ...style }}>
      {children}
    </select>
  )
}

/** "1–10 of 23  Previous · Next" under a long list. Hidden when everything fits on one page. */
export function Pager({ at, size, total, onPage, inset }: { at: number; size: number; total: number; onPage: (page: number) => void; inset?: boolean }) {
  if (total <= size) return null
  const last = Math.max(0, Math.ceil(total / size) - 1)
  const from = at * size + 1
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: inset ? '12px 28px' : '10px 16px', background: T.page }}>
      <span style={{ flex: 1, fontSize: 13.5, color: T.muted }}>{from}–{Math.min(total, from + size - 1)} of {total}</span>
      <Btn small onClick={() => onPage(at - 1)} disabled={at === 0}>Previous</Btn>
      <Btn small onClick={() => onPage(at + 1)} disabled={at >= last}>Next</Btn>
    </div>
  )
}


/** A save the server refused (attributesData.saveError) — shown at the top of every attribute page. */
function SaveErrorBanner() {
  const { saveError, clearSaveError } = useAttrStatus()
  if (!saveError) return null
  return (
    <div role="alert" style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', borderRadius: 10, background: T.redBg, color: T.red, fontSize: 14 }}>
      <span style={{ flex: 1 }}>Couldn’t save: {saveError}</span>
      <button onClick={clearSaveError} aria-label="Dismiss" style={{ border: 'none', background: 'transparent', color: T.red, cursor: 'pointer', fontSize: 18 }}>×</button>
    </div>
  )
}

/**
 * Renders its page only once the organisation's attributes have loaded (6 Oct 2026) — an edit
 * page opened directly would otherwise start from "not found" and an empty form.
 */
export function AttrGate({ children, back, backLabel, title, skeleton }: {
  children: ReactNode; back?: string; backLabel?: string
  /** The page's own title while it loads (7 Oct 2026) — the heading used to read "Loading…". */
  title?: string
  /** The page's own shape while it loads; a plain card skeleton if not given. */
  skeleton?: ReactNode
}) {
  useFreshAttrs()
  const { ready, error, reload } = useAttrStatus()
  if (ready) return <>{children}</>
  return (
    <OrgPage title={error ? 'Couldn’t load attributes' : title ?? 'Loading…'} back={back} backLabel={backLabel}>
      {error
        ? <div style={{ display: 'flex', alignItems: 'center', gap: 12, fontSize: 14.5, color: T.ink }}>{error}<Btn onClick={() => void reload()}>Try again</Btn></div>
        : <div role="status" aria-label="Loading">{skeleton ?? <CardSkeleton />}</div>}
    </OrgPage>
  )
}

/** A white card standing in for a section: a heading line, then three-across fields. */
export function SectionSkeleton({ fields = 6, three = true }: { fields?: number; three?: boolean }) {
  return (
    <section style={{ background: '#fff', border: `1px solid ${T.border}`, borderRadius: 16, padding: '24px 28px 28px', display: 'flex', flexDirection: 'column', gap: 22 }}>
      <Skeleton w={190} h={16} />
      <div style={{ display: 'grid', gridTemplateColumns: three ? 'repeat(auto-fill, minmax(max(240px, calc((100% - 56px) / 3)), 1fr))' : 'repeat(auto-fill, minmax(260px, 1fr))', gap: '22px 28px' }}>
        {Array.from({ length: fields }, (_, i) => (
          <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <Skeleton w={`${45 + (i % 3) * 12}%`} h={12} />
            <Skeleton h={44} r={10} />
          </div>
        ))}
      </div>
    </section>
  )
}

/** A list card standing in for rows (groups, people). */
export function RowsSkeleton({ rows = 4 }: { rows?: number }) {
  return (
    <div style={{ background: '#fff', border: `1px solid ${T.border}`, borderRadius: 16, overflow: 'hidden' }}>
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '18px 28px', borderBottom: '1px solid #F2F0F5' }}>
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}><Skeleton w={`${38 - i * 4}%`} h={13} /><Skeleton w={`${55 - i * 5}%`} h={11} /></div>
          <Skeleton w={70} h={24} r={999} />
          <Skeleton w={24} h={24} r={6} />
        </div>
      ))}
    </div>
  )
}

function CardSkeleton() {
  return <div style={{ display: 'flex', flexDirection: 'column', gap: 28 }}><SectionSkeleton fields={3} /><RowsSkeleton rows={3} /></div>
}
